/**
 * @file Header group behaviour (sections/header.liquid, snippets/header-menu.liquid,
 * sections/announcement-bar.liquid). Loaded as a module where those render.
 *
 * <bk-header>
 *   - Sticky modes from data-sticky: none | always | scroll_up (the section wrapper moves with a
 *     transform only). data-scroll-state: top | scrolled | hidden ("scrolled" swaps the hairline for
 *     the warm elevation; "hidden" slides the bar away while scrolling down; it never hides while a
 *     panel is open, a dialog is open or keyboard focus is inside).
 *   - Writes --header-h (bar height) and --header-offset (visible sticky height, 0 when hidden or not
 *     sticky) on <html> for sticky toolbars and scroll margins.
 *   - Sets data-nav-overflow when the desktop menu does not fit, so the burger and drawer take over.
 *   - Cart: keeps [data-cart-count] and the [data-cart-label] sentence in sync on bk:cart:update and
 *     after a back/forward cache restore; the label is chosen with Intl.PluralRules from the
 *     server-rendered samples in data-cart-labels. The cart link opens <bk-cart-drawer> when
 *     data-cart-type="drawer" (plain clicks only; the link works without JS).
 * <bk-header-menu>
 *   - Disclosure navigation: click, Enter or Space toggles a panel; Escape closes it and returns
 *     focus to its button; focus leaving the item closes it; Left/Right (mirrored in RTL), Home and
 *     End move between top items; Down/Up open a panel and focus its first/last link; Up/Down move
 *     inside a panel. Hover opens after data-hover-delay ms on fine pointers only. Outside clicks
 *     close. data-open is set while a panel is open (frosted scrim).
 *   - Moves each [data-mega-promo] into the panel named by its data-menu-item.
 *   - Theme editor: selecting a block inside a panel opens that panel until it is deselected.
 * <bk-announcement-bar>
 *   - rotate: one announcement at a time, crossfade every data-interval ms; marquee: a cloned,
 *     inert copy makes the ticker loop seamless. Layout per breakpoint (data-layout-mobile /
 *     data-layout-desktop). Pauses with the button, on hover, on focus and in a hidden tab; stays
 *     still under prefers-reduced-motion. Button labels come from data-label-pause / data-label-play.
 */
import { fetchCart, prefersReducedMotion, subscribe } from '@theme/utils';

/**
 * matches() that answers false instead of throwing on a selector the browser does not know
 * (:focus-visible before Safari 15.4, :modal before Safari 15.6 / Chrome 105).
 * @param {Element | null} element
 * @param {string} selector
 */
function safeMatches(element, selector) {
  try {
    return Boolean(element?.matches(selector));
  } catch {
    return false;
  }
}

const DESKTOP_NAV_QUERY = '(min-width: 990px)';
const TABLET_QUERY = '(min-width: 750px)';
const SCROLL_TOLERANCE = 6;
/* How far the visitor must scroll back up before a hidden bar returns (a thumb drifts a few px). */
const REVEAL_DISTANCE = 120;

/**
 * Set a custom property on <html> only when it changes: every write invalidates the styles of the
 * whole page, even when the value is the same.
 * @param {string} name
 * @param {string} value
 */
function setRootProperty(name, value) {
  const root = document.documentElement.style;
  if (root.getPropertyValue(name) !== value) root.setProperty(name, value);
}

/**
 * Decode the few HTML entities the `t` filter can leave in a JSON-encoded label.
 * @param {string} value
 * @returns {string}
 */
function decodeEntities(value) {
  if (!value.includes('&')) return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

/**
 * @param {Element} element
 * @returns {boolean}
 */
function isRtl(element) {
  return getComputedStyle(element).direction === 'rtl';
}

class BkHeader extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {(() => void) | null} */
  #unsubscribe = null;

  #frame = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #animationTimer;

  #lastScrollY = 0;

  #upDistance = 0;

  #naturalTop = 0;

  #barHeight = 0;

  #desktop = matchMedia(DESKTOP_NAV_QUERY);

  /** @type {Record<string, string>} */
  #labels = {};

  /** @type {Intl.PluralRules | null} */
  #plural = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#labels = this.#readLabels();
    try {
      this.#plural = new Intl.PluralRules(document.documentElement.lang || undefined);
    } catch {
      this.#plural = null;
    }

    this.#lastScrollY = window.scrollY;
    if (this.dataset.sticky && this.dataset.sticky !== 'none') {
      window.addEventListener('scroll', () => this.#requestUpdate(), { passive: true, signal });
    }

    // The first measure happens in the observer's first callback, after the browser's own layout:
    // measuring here would force an extra layout of the whole page while it is still loading.
    // Reads come before writes in the callback: a custom property set on <html> invalidates the
    // styles of the whole page, so a read after it would force a full recalculation (measured:
    // two forced recalcs of 2,700 elements, 640 ms on a Lighthouse phone).
    this.#resizeObserver = new ResizeObserver(() => {
      const y = window.scrollY;
      this.#onResize();
      this.#update(y);
    });
    this.#resizeObserver.observe(this);
    this.#desktop.addEventListener('change', () => this.#checkNavOverflow(), { signal });

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('focusin', () => this.#reveal(), { signal });
    window.addEventListener('pageshow', (event) => this.#onPageShow(event), { signal });
    document.addEventListener('bk:dialog:open', () => this.#closeMenu(), { signal });
    this.#unsubscribe = subscribe('bk:cart:update', (detail) => this.#onCartUpdate(detail));

    // No #update() here: reading scrollY while the page is still parsing forces a layout. The
    // observer's first callback runs right after the browser's own first layout and does it.
    // Web fonts change the menu's width without resizing the header: measure again once they load.
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#checkNavOverflow();
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    clearTimeout(this.#animationTimer);
  }

  /** @returns {Record<string, string>} */
  #readLabels() {
    try {
      const parsed = JSON.parse(this.dataset.cartLabels ?? '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  #requestUpdate() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  }

  /** @returns {boolean} True while hiding the header would get in the visitor's way. */
  #isBusy() {
    if (this.querySelector('bk-header-menu[data-open]')) return true;
    if (safeMatches(document.activeElement, ':focus-visible') && this.contains(document.activeElement)) return true;
    return document.documentElement.classList.contains('bk-scroll-lock');
  }

  /** @param {number} [scrollY] The scroll position, when the caller already read it. */
  #update(scrollY = window.scrollY) {
    const y = Math.max(0, scrollY);
    const sticky = this.dataset.sticky ?? 'none';
    const previous = this.dataset.scrollState ?? 'top';
    let state = previous;

    if (sticky === 'none' || y <= this.#naturalTop) {
      state = 'top';
      this.#lastScrollY = y;
      this.#upDistance = 0;
    } else if (sticky === 'always') {
      state = 'scrolled';
      this.#lastScrollY = y;
    } else {
      const delta = y - this.#lastScrollY;
      if (Math.abs(delta) < SCROLL_TOLERANCE) {
        if (previous === 'top') state = 'scrolled';
      } else {
        this.#lastScrollY = y;
        const pastHeader = y > this.#naturalTop + this.#barHeight;
        if (delta > 0) {
          this.#upDistance = 0;
          state = pastHeader && !this.#isBusy() ? 'hidden' : 'scrolled';
        } else {
          // A thumb drifts upward constantly while reading. Bringing the bar back on every small
          // drift leaves it on screen for the whole visit, which reads as a bar stuck to the top,
          // so a hidden bar waits for a deliberate scroll back up.
          this.#upDistance += -delta;
          state = previous === 'hidden' && this.#upDistance < REVEAL_DISTANCE ? 'hidden' : 'scrolled';
        }
      }
    }

    if (state !== previous) this.#setState(state);
  }

  /** @param {string} state */
  #setState(state) {
    const previous = this.dataset.scrollState;
    this.dataset.scrollState = state;
    // Mirrored on the section wrapper for browsers without :has() (see header.liquid).
    this.parentElement?.classList.toggle('header-section--hidden', state === 'hidden');
    // will-change only while the bar actually slides (to or from hidden): a transformed ancestor
    // would otherwise re-anchor the fixed menu scrim for the length of the animation.
    if (state === 'hidden' || previous === 'hidden') {
      this.setAttribute('data-animating', '');
      this.parentElement?.classList.add('header-section--animating');
      clearTimeout(this.#animationTimer);
      this.#animationTimer = setTimeout(() => {
        this.removeAttribute('data-animating');
        this.parentElement?.classList.remove('header-section--animating');
      }, 400);
    }
    if (state === 'hidden') this.#closeMenu();
    this.#writeOffset();
  }

  /** Keyboard focus entering a hidden header brings it back. */
  #reveal() {
    if (this.dataset.scrollState === 'hidden') this.#setState('scrolled');
  }

  #onResize() {
    // All reads first...
    const bar = this.querySelector('.header__bar');
    this.#barHeight = Math.round(bar ? bar.getBoundingClientRect().height : this.offsetHeight);
    this.#naturalTop = this.#measureNaturalTop();
    // ...then the writes.
    setRootProperty('--header-h', `${this.#barHeight}px`);
    this.#checkNavOverflow();
    this.#writeOffset();
  }

  #writeOffset() {
    const sticky = this.dataset.sticky ?? 'none';
    const visible = sticky !== 'none' && this.dataset.scrollState !== 'hidden';
    setRootProperty('--header-offset', visible ? `${this.#barHeight}px` : '0px');
  }

  /**
   * Distance from the top of the document to the header when it is not stuck: the height of the
   * in-flow sections before it (the announcement bar).
   * @returns {number}
   */
  #measureNaturalTop() {
    const section = this.parentElement;
    if (!section) return 0;
    let top = 0;
    for (let sibling = section.previousElementSibling; sibling; sibling = sibling.previousElementSibling) {
      const { position, display } = getComputedStyle(sibling);
      if (display === 'none' || position === 'absolute' || position === 'fixed') continue;
      top += sibling.getBoundingClientRect().height;
    }
    return Math.round(top);
  }

  /** Desktop only: collapse to the burger when the menu is wider than the space it has. */
  #checkNavOverflow() {
    const nav = this.querySelector('[data-header-nav]');
    const list = nav?.querySelector('.header-menu__list');
    // Phones have no menu row: nothing to measure, and no attribute write to dirty the styles.
    if (!(nav instanceof HTMLElement) || !(list instanceof HTMLElement) || !this.#desktop.matches) {
      if (this.hasAttribute('data-nav-overflow')) this.removeAttribute('data-nav-overflow');
      return;
    }
    this.removeAttribute('data-nav-overflow');
    const overflow = list.scrollWidth > nav.clientWidth + 1;
    if (overflow) {
      this.#closeMenu();
      this.setAttribute('data-nav-overflow', '');
    }
  }

  #closeMenu() {
    const menu = /** @type {any} */ (this.querySelector('bk-header-menu'));
    if (menu && typeof menu.close === 'function') menu.close();
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target;
    const link = target instanceof Element ? target.closest('[data-cart-link]') : null;
    if (!link || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (this.dataset.cartType !== 'drawer') return;

    const drawer = /** @type {any} */ (document.querySelector('bk-cart-drawer'));
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(link);
  }

  /** @param {PageTransitionEvent} event */
  #onPageShow(event) {
    if (event.persisted) this.#refreshCart();
  }

  /** @param {any} detail */
  #onCartUpdate(detail) {
    const count = detail?.cart?.item_count;
    if (typeof count === 'number') {
      this.#renderCount(count, true);
      return;
    }
    this.#refreshCart();
  }

  async #refreshCart() {
    try {
      const cart = await fetchCart();
      if (typeof cart?.item_count === 'number') this.#renderCount(cart.item_count, false);
    } catch {
      // Keep the server-rendered count.
    }
  }

  /**
   * Sync every count bubble on the page, not only the header's: the mobile tab bar renders its own
   * [data-cart-count] in the footer group (PLAN contract: "[data-cart-count] (every count bubble)").
   * Zero clears the text as well as setting [hidden], so both the header rule and the tab bar's
   * :empty rule hide it.
   * @param {number} count
   * @param {boolean} animate
   */
  #renderCount(count, animate) {
    for (const bubble of document.querySelectorAll('[data-cart-count]')) {
      if (!(bubble instanceof HTMLElement)) continue;
      bubble.textContent = count > 0 ? String(count) : '';
      bubble.hidden = count === 0;
      if (animate && count > 0 && !prefersReducedMotion()) {
        bubble.classList.remove('header__cart-count--bump');
        void bubble.offsetWidth;
        bubble.classList.add('header__cart-count--bump');
      }
    }

    const label = this.#labelFor(count);
    if (!label) return;
    for (const element of document.querySelectorAll('[data-cart-label]')) element.textContent = label;
  }

  /**
   * The cart sentence for `count`, from the sample whose plural category matches.
   * @param {number} count
   * @returns {string | null}
   */
  #labelFor(count) {
    const samples = Object.keys(this.#labels);
    if (samples.length === 0) return null;

    let sample = samples.find((key) => Number(key) === count);
    if (!sample && this.#plural) {
      const category = this.#plural.select(count);
      sample = samples.find((key) => this.#plural?.select(Number(key)) === category);
    }
    sample ??= samples[samples.length - 1];

    const template = decodeEntities(String(this.#labels[sample] ?? ''));
    return template.replace(new RegExp(`(^|\\D)${sample}(?!\\d)`), `$1${count}`);
  }
}

class BkHeaderMenu extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {HTMLButtonElement | null} */
  #openTrigger = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverTimer;

  #openedByHover = false;

  #editorOpen = false;

  #finePointer = matchMedia('(hover: hover) and (pointer: fine)');

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#placePromos();

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    this.addEventListener('focusout', (event) => this.#onFocusout(event), { signal });
    this.addEventListener('pointerover', (event) => this.#onPointerOver(event), { signal });
    this.addEventListener('pointerout', (event) => this.#onPointerOut(event), { signal });
    document.addEventListener('click', (event) => this.#onDocumentClick(event), { signal });
    document.addEventListener('shopify:block:select', (event) => this.#onEditorSelect(event), { signal });
    document.addEventListener('shopify:block:deselect', () => this.#onEditorDeselect(), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    clearTimeout(this.#hoverTimer);
  }

  /** @returns {boolean} */
  get isOpen() {
    return this.#openTrigger !== null;
  }

  /**
   * Open the panel controlled by `trigger` (closing any other).
   * @param {HTMLButtonElement} trigger
   * @param {{ focus?: 'first' | 'last' | null, byHover?: boolean }} [options]
   */
  open(trigger, { focus = null, byHover = false } = {}) {
    if (this.#openTrigger && this.#openTrigger !== trigger) this.close();
    const panel = this.#panelOf(trigger);
    if (!panel) return;

    trigger.setAttribute('aria-expanded', 'true');
    this.#openTrigger = trigger;
    this.#openedByHover = byHover;
    this.setAttribute('data-open', '');

    if (!focus) return;
    const links = this.#panelLinks(panel);
    const target = focus === 'last' ? links[links.length - 1] : links[0];
    target?.focus();
  }

  /**
   * Close the open panel.
   * @param {{ restoreFocus?: boolean }} [options]
   */
  close({ restoreFocus = false } = {}) {
    clearTimeout(this.#hoverTimer);
    const trigger = this.#openTrigger;
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', 'false');
    this.#openTrigger = null;
    this.#openedByHover = false;
    this.#editorOpen = false;
    this.removeAttribute('data-open');
    if (restoreFocus) trigger.focus();
  }

  /** @returns {HTMLElement[]} */
  #topItems() {
    return [...this.querySelectorAll('[data-menu-top]')].filter((item) => item instanceof HTMLElement);
  }

  /**
   * @param {Element} trigger
   * @returns {HTMLElement | null}
   */
  #panelOf(trigger) {
    const id = trigger.getAttribute('aria-controls');
    if (!id) return null;
    const panel = this.querySelector(`#${CSS.escape(id)}`);
    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * @param {HTMLElement} panel
   * @returns {HTMLElement[]}
   */
  #panelLinks(panel) {
    const found = [];
    for (const element of panel.querySelectorAll('a[href], button:not([disabled])')) {
      if (element instanceof HTMLElement && element.getClientRects().length > 0) found.push(element);
    }
    return found;
  }

  /** Put each promotion block in the panel of the menu item it names. */
  #placePromos() {
    const triggers = [...this.querySelectorAll('[data-menu-trigger]')];
    for (const promo of this.querySelectorAll('[data-mega-promo]')) {
      const wanted = (promo.getAttribute('data-menu-item') ?? '').trim();
      if (!wanted) continue;
      const trigger = triggers.find((item) => item.getAttribute('data-menu-title') === wanted);
      const slot = trigger ? this.#panelOf(trigger)?.querySelector('[data-promo-slot]') : null;
      if (slot && !slot.contains(promo)) slot.append(promo);
    }
    for (const slot of this.querySelectorAll('[data-promo-slot]')) {
      if (!slot.querySelector('[data-mega-promo]')) slot.replaceChildren();
    }
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target;
    const trigger = target instanceof Element ? target.closest('[data-menu-trigger]') : null;
    if (!(trigger instanceof HTMLButtonElement) || !this.contains(trigger)) return;

    if (this.#openTrigger !== trigger) {
      this.open(trigger);
      return;
    }
    // A click right after a hover-open keeps the panel open; the next click closes it.
    if (this.#openedByHover) {
      this.#openedByHover = false;
      return;
    }
    this.close();
  }

  /** @param {KeyboardEvent} event */
  #onKeydown(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (event.key === 'Escape') {
      if (!this.#openTrigger) return;
      event.preventDefault();
      this.close({ restoreFocus: true });
      return;
    }

    if (target.hasAttribute('data-menu-top')) {
      this.#onTopKeydown(event, target);
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const panel = target.closest('[data-menu-panel]');
    if (!(panel instanceof HTMLElement)) return;
    const links = this.#panelLinks(panel);
    const index = links.indexOf(target);
    if (index === -1) return;
    event.preventDefault();
    const step = event.key === 'ArrowDown' ? 1 : -1;
    links[(index + step + links.length) % links.length]?.focus();
  }

  /**
   * @param {KeyboardEvent} event
   * @param {HTMLElement} item - Focused top-level button or link
   */
  #onTopKeydown(event, item) {
    const items = this.#topItems();
    const index = items.indexOf(item);
    const rtl = isRtl(this);
    const nextKey = rtl ? 'ArrowLeft' : 'ArrowRight';
    const previousKey = rtl ? 'ArrowRight' : 'ArrowLeft';

    switch (event.key) {
      case nextKey:
        event.preventDefault();
        items[(index + 1) % items.length]?.focus();
        break;
      case previousKey:
        event.preventDefault();
        items[(index - 1 + items.length) % items.length]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        items[items.length - 1]?.focus();
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        if (!(item instanceof HTMLButtonElement) || !item.hasAttribute('data-menu-trigger')) return;
        event.preventDefault();
        this.open(item, { focus: event.key === 'ArrowDown' ? 'first' : 'last' });
        break;
      default:
    }
  }

  /** @param {FocusEvent} event */
  #onFocusout(event) {
    if (!this.#openTrigger || this.#editorOpen) return;
    const next = event.relatedTarget;
    // Focus going nowhere (a click on plain text, the window losing focus) keeps the panel;
    // clicks outside are handled by #onDocumentClick.
    if (!(next instanceof Node)) return;
    const item = this.#openTrigger.closest('.header-menu__item');
    if (item?.contains(next)) return;
    this.close();
  }

  /** @param {MouseEvent} event */
  #onDocumentClick(event) {
    if (!this.#openTrigger || this.#editorOpen) return;
    const target = event.target;
    if (target instanceof Node && this.contains(target)) return;
    this.close();
  }

  /** @param {PointerEvent} event */
  #onPointerOver(event) {
    if (event.pointerType !== 'mouse' || !this.#finePointer.matches) return;
    const item = event.target instanceof Element ? event.target.closest('.header-menu__item') : null;
    clearTimeout(this.#hoverTimer);
    if (!item || !this.contains(item)) return;

    const trigger = item.querySelector(':scope > [data-menu-trigger]');
    if (trigger && trigger === this.#openTrigger) return;

    const delay = Number(this.dataset.hoverDelay ?? 150);
    this.#hoverTimer = setTimeout(() => {
      if (trigger instanceof HTMLButtonElement) this.open(trigger, { byHover: true });
      else if (this.#openedByHover) this.close();
    }, delay);
  }

  /** @param {PointerEvent} event */
  #onPointerOut(event) {
    if (event.pointerType !== 'mouse' || !this.#finePointer.matches) return;
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    clearTimeout(this.#hoverTimer);
    if (!this.#openedByHover) return;
    this.#hoverTimer = setTimeout(() => {
      if (this.#openedByHover) this.close();
    }, 250);
  }

  /** @param {Event} event */
  #onEditorSelect(event) {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    const panel = target.closest('[data-menu-panel]');
    if (!panel) return;
    const trigger = this.querySelector(`[aria-controls="${CSS.escape(panel.id)}"]`);
    if (!(trigger instanceof HTMLButtonElement)) return;
    this.open(trigger);
    this.#editorOpen = true;
  }

  #onEditorDeselect() {
    if (this.#editorOpen) this.close();
  }
}

class BkAnnouncementBar extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  #index = 0;

  #userPaused = false;

  #editorPaused = false;

  #hovered = false;

  #focused = false;

  #tablet = matchMedia(TABLET_QUERY);

  #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    if (this.dataset.layoutMobile === 'marquee' || this.dataset.layoutDesktop === 'marquee') this.#prepareMarquee();

    const toggle = this.querySelector('[data-announcement-toggle]');
    toggle?.addEventListener('click', () => this.#setUserPaused(!this.#userPaused), { signal });
    this.addEventListener('pointerenter', () => this.#setHovered(true), { signal });
    this.addEventListener('pointerleave', () => this.#setHovered(false), { signal });
    this.addEventListener('focusin', () => this.#setFocused(true), { signal });
    this.addEventListener('focusout', (event) => this.#onFocusout(event), { signal });
    document.addEventListener('visibilitychange', () => this.#sync(), { signal });
    this.#tablet.addEventListener('change', () => this.#sync(), { signal });
    this.#reducedMotion.addEventListener('change', () => this.#sync(), { signal });
    document.addEventListener('shopify:block:select', (event) => this.#onEditorSelect(event), { signal });
    document.addEventListener('shopify:block:deselect', () => this.#onEditorDeselect(), { signal });

    this.#show(0);
    this.#sync();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#stop();
  }

  /** @returns {string} */
  #layout() {
    return (this.#tablet.matches ? this.dataset.layoutDesktop : this.dataset.layoutMobile) ?? 'static';
  }

  /** @returns {HTMLElement[]} */
  #items() {
    return [...this.querySelectorAll('[data-announcement-group] > [data-announcement]')].filter(
      (item) => item instanceof HTMLElement,
    );
  }

  #sync() {
    // Blocks with blank text render nothing, so the server-side count can promise more than shows:
    // with fewer than two visible announcements nothing moves and the pause control goes away.
    const single = this.#items().length < 2;
    this.toggleAttribute('data-single', single);
    const layout = single ? 'static' : this.#layout();
    const paused = this.#userPaused || this.#editorPaused;
    this.toggleAttribute('data-paused', paused);

    const label = this.querySelector('[data-announcement-toggle-label]');
    const text = paused ? this.dataset.labelPlay : this.dataset.labelPause;
    if (label && text) label.textContent = text;

    const track = this.querySelector('[data-announcement-track]');
    if (layout === 'static') track?.removeAttribute('aria-live');
    else track?.setAttribute('aria-live', paused ? 'polite' : 'off');

    const rotate =
      layout === 'rotate' &&
      !paused &&
      !this.#hovered &&
      !this.#focused &&
      !this.#reducedMotion.matches &&
      document.visibilityState === 'visible';
    if (rotate) this.#start();
    else this.#stop();
  }

  #start() {
    if (this.#timer) return;
    const interval = Math.max(5000, Number(this.dataset.interval) || 5000);
    this.#timer = setInterval(() => this.#show(this.#index + 1), interval);
  }

  #stop() {
    clearInterval(this.#timer);
    this.#timer = undefined;
  }

  /** @param {number} index */
  #show(index) {
    const items = this.#items();
    if (items.length === 0) return;
    this.#index = (index + items.length) % items.length;
    for (const [position, item] of items.entries()) item.toggleAttribute('data-active', position === this.#index);
  }

  /** @param {boolean} value */
  #setUserPaused(value) {
    this.#userPaused = value;
    this.#sync();
  }

  /** @param {boolean} value */
  #setHovered(value) {
    this.#hovered = value;
    this.#sync();
  }

  /** @param {boolean} value */
  #setFocused(value) {
    this.#focused = value;
    this.#sync();
  }

  /** @param {FocusEvent} event */
  #onFocusout(event) {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    this.#setFocused(false);
  }

  /** Clone the announcements once so the ticker loops without a gap; the copy is inert and hidden. */
  #prepareMarquee() {
    const track = this.querySelector('[data-announcement-track]');
    const group = track?.querySelector('[data-announcement-group]');
    if (!track || !(group instanceof HTMLElement) || track.querySelector('.announcement-bar__group--clone')) return;

    const clone = /** @type {HTMLElement} */ (group.cloneNode(true));
    clone.classList.add('announcement-bar__group--clone');
    clone.removeAttribute('data-announcement-group');
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    for (const element of [clone, ...clone.querySelectorAll('*')]) {
      element.removeAttribute('id');
      for (const attribute of [...element.attributes]) {
        if (attribute.name.startsWith('data-shopify')) element.removeAttribute(attribute.name);
      }
    }
    track.append(clone);

    // First measure in the observer's first callback, not here (no forced layout while loading).
    this.#resizeObserver = new ResizeObserver(() => this.#measureViewport());
    this.#resizeObserver.observe(this);
  }

  #measureViewport() {
    const viewport = this.querySelector('.announcement-bar__viewport');
    if (!viewport) return;
    this.style.setProperty('--announcement-viewport', `${Math.ceil(viewport.getBoundingClientRect().width)}px`);
  }

  /** @param {Event} event */
  #onEditorSelect(event) {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    const index = this.#items().findIndex((item) => item === target || item.contains(target));
    if (index !== -1) this.#show(index);
    this.#editorPaused = true;
    this.#sync();
  }

  #onEditorDeselect() {
    if (!this.#editorPaused) return;
    this.#editorPaused = false;
    this.#sync();
  }
}

if (!customElements.get('bk-header')) customElements.define('bk-header', BkHeader);
if (!customElements.get('bk-header-menu')) customElements.define('bk-header-menu', BkHeaderMenu);
if (!customElements.get('bk-announcement-bar')) customElements.define('bk-announcement-bar', BkAnnouncementBar);
